import { ICONS } from './icons';
import { etAndLocal } from './time';
import React, { useState, useRef, useEffect, useId, useCallback } from 'react';

export const cx = (...a) => a.filter(Boolean).join(' ');

/* ---------- Icon (lucide nodes, 1.5px stroke, 16/20px) ---------- */
export function Icon({ name, size = 16, className, label, strokeWidth, style }) {
  if (name && typeof name !== 'string') {
    return React.createElement(name, { size, strokeWidth: strokeWidth || 1.5, className: cx('sn-icon', className), style, 'aria-hidden': label ? undefined : 'true', 'aria-label': label });
  }
  const nodes = ICONS[name];
  if (!nodes) return null;
  return (
    <svg className={cx('sn-icon', className)} width={size} height={size} viewBox="0 0 24 24" style={{ ...(strokeWidth ? { strokeWidth } : null), ...style }}
      aria-hidden={label ? undefined : 'true'} role={label ? 'img' : undefined} aria-label={label} focusable="false">
      {nodes.map(([tag, attrs], i) => React.createElement(tag, { key: i, ...attrs }))}
    </svg>
  );
}

/* ---------- Logo ---------- */
export function LogoMark({ size = 20, title = 'Sentinel' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label={title} style={{ flex: 'none', display: 'block' }}>
      <g stroke="var(--brand)" fill="none" strokeWidth={size <= 16 ? 2.4 : 2}>
        <circle cx="12" cy="12" r="8.25" />
        <path d="M12 0.15V3.15M12 23.85V20.85M0.15 12H3.15M23.85 12H20.85" />
        <path d="M8.6 12.2L11 14.6L15.6 9.9" strokeLinecap="square" strokeLinejoin="miter" />
      </g>
    </svg>
  );
}
export function Logo({ size = 20, showWordmark = true, version }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <LogoMark size={size} />
      {showWordmark && <span style={{ fontWeight: 600, fontSize: Math.round(size * 0.8), letterSpacing: '0.01em', color: 'var(--text-primary)' }}>Sentinel</span>}
      {version && <VersionChip {...version} />}
    </span>
  );
}

/* ---------- Button family ---------- */
export function Button({ variant = 'secondary', size = 'md', icon, iconRight, loading = false, block, children, className, disabled, ...rest }) {
  return (
    <button type="button" {...rest} disabled={disabled || loading} aria-busy={loading || undefined}
      className={cx('sn-btn', 'sn-btn-' + variant, size !== 'md' && 'sn-btn-' + size, block && 'sn-btn-block', className)}>
      {loading ? <Icon name="Loader" className="sn-spin" /> : icon && <Icon name={icon} />}
      {children}
      {iconRight && !loading && <Icon name={iconRight} />}
    </button>
  );
}
export function IconButton({ icon, label, size = 'md', variant = 'ghost', badge, className, ...rest }) {
  return (
    <button type="button" aria-label={label} title={label} {...rest}
      className={cx('sn-btn', 'sn-btn-' + variant, 'sn-btn-icon', size !== 'md' && 'sn-btn-' + size, badge != null && 'sn-bell', className)}>
      <Icon name={icon} size={size === 'lg' ? 20 : 16} />
      {badge != null && badge > 0 && <span className="sn-count" aria-hidden="true">{badge}</span>}
      {badge != null && badge > 0 && <span className="sn-vh">{badge} unread</span>}
    </button>
  );
}
/** The acknowledgement control. It records that a human reviewed an ENTER; it never sends an order. */
export function ConfirmEnterButton({ confirmed = false, confirmedAt, disabled, onConfirm, candidateId }) {
  const nid = useId();
  return (
    <button type="button" className="sn-btn sn-btn-ack" aria-pressed={confirmed} disabled={disabled}
      aria-describedby={nid} onClick={() => { if (!confirmed && onConfirm) onConfirm(candidateId); }}>
      <span className="sn-ack-main"><Icon name={confirmed ? 'ClipboardCheck' : 'Check'} />{confirmed ? 'ENTER acknowledged' : 'Confirm ENTER'}<span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— acknowledgement only</span></span>
      <span className="sn-ack-sub" id={nid}>{confirmed ? `Logged ${confirmedAt} · no order was sent` : 'Records your review in the audit trail. No order is placed.'}</span>
    </button>
  );
}
export function Kbd({ children }) { return <kbd className="sn-kbd">{children}</kbd>; }

/* ---------- Fields ---------- */
export function TextField({ label, hint, error, icon, suffix, numeric, required, disabled, id, className, ...rest }) {
  const auto = useId(); const fid = id || auto;
  return (
    <div className={cx('sn-field', className)}>
      {label && <label className="sn-field-label" htmlFor={fid}>{label}{required && <span className="sn-req">Required</span>}</label>}
      <div className="sn-input-wrap" data-invalid={!!error} data-disabled={!!disabled}>
        {icon && <Icon name={icon} />}
        <input id={fid} className={cx('sn-input', numeric && 'sn-num')} disabled={disabled} aria-invalid={!!error || undefined}
          aria-describedby={error ? fid + '-e' : hint ? fid + '-h' : undefined} required={required} {...rest} />
        {suffix && <span className="sn-input-suffix">{suffix}</span>}
      </div>
      {error ? <span className="sn-error" id={fid + '-e'}><Icon name="TriangleAlert" size={14} />{error}</span>
        : hint && <span className="sn-help" id={fid + '-h'}>{hint}</span>}
    </div>
  );
}

function useDismiss(open, setOpen, ref) {
  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
}

export function Select({ label, options = [], value, onChange, defaultOpen = false, hint, width, disabled, className, ...rest }) {
  const [open, setOpen] = useState(defaultOpen);
  const [inner, setVal] = useState(value ?? options[0]?.value);
  const val = value !== undefined ? value : inner;
  const [active, setActive] = useState(Math.max(0, options.findIndex(o => o.value === (value ?? options[0]?.value))));
  const ref = useRef(null); const id = useId();
  useDismiss(open, setOpen, ref);
  const cur = options.find(o => o.value === val);
  const pick = i => { setVal(options[i].value); onChange && onChange(options[i].value); setOpen(false); };
  const onKey = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) setOpen(true); else setActive(a => Math.min(options.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open ? pick(active) : setOpen(true); }
    else if (e.key === 'Home') setActive(0); else if (e.key === 'End') setActive(options.length - 1);
  };
  return (
    <div className={cx('sn-field', className)} ref={ref} style={{ position: 'relative', width }}>
      {label && <span className="sn-field-label" id={id + '-l'}>{label}</span>}
      <button type="button" {...rest} disabled={disabled} className="sn-input-wrap sn-select" role="combobox" aria-haspopup="listbox" aria-expanded={open}
        aria-labelledby={label ? id + '-l' : undefined} aria-controls={id + '-lb'} aria-activedescendant={open ? `${id}-o${active}` : undefined}
        onClick={() => setOpen(o => !o)} onKeyDown={onKey}>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{cur?.icon && <Icon name={cur.icon} />}{cur?.label}</span>
        <Icon name="ChevronsUpDown" size={14} style={{ color: 'var(--text-tertiary)' }} />
      </button>
      {open && (
        <ul className="sn-menu" role="listbox" id={id + '-lb'} style={{ top: label ? 'calc(100% + 4px)' : undefined }}>
          {options.map((o, i) => (
            <li key={o.value} id={`${id}-o${i}`} role="option" aria-selected={o.value === val} data-active={i === active}
              className="sn-menu-item" onMouseEnter={() => setActive(i)} onMouseDown={e => { e.preventDefault(); pick(i); }}>
              {o.icon && <Icon name={o.icon} />}{o.label}{o.meta && <span className="sn-menu-meta">{o.meta}</span>}<Icon name="Check" className="sn-check" />
            </li>
          ))}
        </ul>
      )}
      {hint && <span className="sn-help">{hint}</span>}
    </div>
  );
}

/* Session date picker: trading-calendar aware (holidays struck through, early closes dotted). */
/** `value` is a YYYY-MM-DD session date; without one the picker shows an em dash and opens on the current month. */
export function DatePicker({ label = 'Session date', value, defaultOpen = false, month: monthProp, holidays = [], earlyCloses = [], inline = false, onChange }) {
  const [open, setOpen] = useState(defaultOpen || inline);
  const [sel, setSel] = useState(value ?? null);
  const month = monthProp || (() => {
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(value || '');
    if (m) return { y: Number(m[1]), m: Number(m[2]) };
    const n = new Date(); return { y: n.getUTCFullYear(), m: n.getUTCMonth() + 1 };
  })();
  const ref = useRef(null); useDismiss(open && !inline, setOpen, ref);
  const first = new Date(Date.UTC(month.y, month.m - 1, 1)); const startDow = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(month.y, month.m, 0)).getUTCDate();
  const cells = []; for (let i = 0; i < startDow; i++) cells.push(null); for (let d = 1; d <= days; d++) cells.push(d);
  const iso = d => `${month.y}-${String(month.m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const fmt = s => { if (!s) return '—'; const d = new Date(s + 'T12:00:00Z'); return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }); };
  const monthName = first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const cal = (
    <div className={cx('sn-pop sn-cal')} role="dialog" aria-label="Choose session date" style={inline ? null : { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40 }}>
      <div className="sn-cal-head"><IconButton icon="ChevronLeft" label="Previous month" size="sm" /><span>{monthName}</span><IconButton icon="ChevronRight" label="Next month" size="sm" /></div>
      <div className="sn-cal-grid" role="grid">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i} className="sn-cal-dow" role="columnheader">{d}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const dow = (startDow + d - 1) % 7; const wk = dow >= 5; const s = iso(d);
          const hol = holidays.includes(s), early = earlyCloses.includes(s);
          return <button key={i} type="button" className="sn-cal-day" role="gridcell" aria-selected={s === sel} tabIndex={s === sel ? 0 : -1}
            data-muted={wk} data-holiday={hol} data-early={early} disabled={wk || hol}
            aria-label={`${fmt(s)}${hol ? ', market holiday' : ''}${early ? `, early close ${etAndLocal('13:00', { day: s })}` : ''}${wk ? ', weekend' : ''}`}
            onClick={() => { setSel(s); onChange && onChange(s); if (!inline) setOpen(false); }}>{d}</button>;
        })}
      </div>
      <div className="sn-cal-legend"><span><s>14</s> holiday</span><span>● early close</span><span>Weekends disabled</span></div>
    </div>
  );
  if (inline) return cal;
  return (
    <div className="sn-field" ref={ref} style={{ position: 'relative' }}>
      {label && <span className="sn-field-label">{label}</span>}
      <button type="button" className="sn-input-wrap sn-select" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Icon name="CalendarDays" /><span className="sn-sans-num">{fmt(sel)}</span></span>
        <Icon name="ChevronDown" size={14} style={{ color: 'var(--text-tertiary)' }} />
      </button>
      {open && cal}
    </div>
  );
}

export function SegmentedControl({ options, value, onChange, label, size, ...rest }) {
  const [inner, setVal] = useState(value ?? options[0].value);
  const val = value !== undefined ? value : inner;
  const refs = useRef([]);
  const idx = options.findIndex(o => o.value === val);
  const move = d => { const n = (idx + d + options.length) % options.length; setVal(options[n].value); onChange && onChange(options[n].value); refs.current[n]?.focus(); };
  return (
    <div {...rest} className="sn-seg" role="radiogroup" aria-label={label} onKeyDown={e => { if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(1); } if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(-1); } }}>
      {options.map((o, i) => (
        <button key={o.value} ref={el => refs.current[i] = el} type="button" role="radio" aria-checked={o.value === val} tabIndex={o.value === val ? 0 : -1}
          className="sn-seg-item" data-testid={o.testId} disabled={o.disabled} onClick={() => { setVal(o.value); onChange && onChange(o.value); }}>
          {o.icon && <Icon name={o.icon} size={14} />}{o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({ label, checked, onChange, disabled, showState = true, ...rest }) {
  const [inner, setOn] = useState(!!checked);
  const on = checked !== undefined ? !!checked : inner;
  return (
    <button type="button" {...rest} role="switch" aria-checked={on} disabled={disabled} className="sn-switch" onClick={() => { setOn(!on); onChange && onChange(!on); }}>
      <span className="sn-switch-track"><span className="sn-switch-thumb" /></span>
      <span>{label}</span>{showState && <span className="sn-switch-state">{on ? 'On' : 'Off'}</span>}
    </button>
  );
}
export function Checkbox({ label, checked, indeterminate = false, onChange, disabled, description, ...rest }) {
  const [inner, setOn] = useState(!!checked);
  const on = checked !== undefined ? !!checked : inner;
  const state = indeterminate ? 'mixed' : on;
  return (
    <button type="button" {...rest} role="checkbox" aria-checked={state} disabled={disabled} className="sn-check-row" onClick={() => { setOn(!on); onChange && onChange(!on); }}>
      <span className="sn-checkbox">{state === 'mixed' ? <Icon name="Minus" size={12} strokeWidth={2.5} /> : on && <Icon name="Check" size={12} strokeWidth={2.5} />}</span>
      <span style={{ display: 'flex', flexDirection: 'column' }}><span>{label}</span>{description && <span className="sn-help">{description}</span>}</span>
    </button>
  );
}

/* ---------- Badge families ---------- */
export function Badge({ children, icon, tone = 'neutral', size = 'md', className, title, ...rest }) {
  return <span {...rest} className={cx('sn-badge', tone !== 'neutral' && 'sn-badge-' + tone, size === 'lg' && 'sn-badge-lg', className)} title={title}>{icon && <Icon name={icon} />}{children}</span>;
}
export const DATA_STATES = {
  LIVE: { icon: null, cls: 'sn-prov-live', desc: 'Real-time feed, within freshness budget' },
  STUB: { icon: 'FlaskRound', cls: 'sn-prov sn-prov-stub', desc: 'Synthetic placeholder data — not market data' },
  DELAYED: { icon: 'Clock', cls: 'sn-prov', desc: 'Delayed feed: values lag the market' },
  STALE: { icon: 'Hourglass', cls: 'sn-prov', desc: 'Last update older than its freshness budget' },
  UNAVAILABLE: { icon: 'CircleSlash', cls: 'sn-prov', desc: 'Source did not respond; values withheld' },
  FROZEN: { icon: 'Lock', cls: 'sn-prov-frozen', desc: 'Sealed checkpoint — immutable' },
  RECALC: { icon: 'RotateCw', cls: 'sn-prov-recalc', desc: 'Appended recalculation sibling; original untouched' },
};
export function DataStateBadge({ state = 'UNAVAILABLE', size = 'md', detail, ...rest }) {
  state = String(state || 'UNAVAILABLE').toUpperCase(); const d = DATA_STATES[state] || DATA_STATES.UNAVAILABLE;
  return (
    <span {...rest} className={cx('sn-badge', d.cls, size === 'lg' && 'sn-badge-lg')} title={d.desc} aria-label={`Data ${state}: ${d.desc}${detail ? ', ' + detail : ''}`}>
      {state === 'LIVE' ? <span className="sn-dot" aria-hidden="true" /> : <Icon name={d.icon} />}{state}{detail && <span style={{ fontWeight: 400, letterSpacing: 0 }}>· {detail}</span>}
    </span>
  );
}
const RULE_ICONS = { PASS: 'CircleCheck', FAIL: 'OctagonX', NA: 'CircleMinus', UNCONFIRMED: 'CircleHelp' };
export function RuleResult({ result = 'NA', children, ...rest }) {
  result = String(result || 'NA').toUpperCase(); if (result === 'N/A') result = 'NA'; if (!RULE_ICONS[result]) result = 'NA';
  return <span {...rest} className={'sn-rule sn-rule-' + result.toLowerCase()}><Icon name={RULE_ICONS[result]} />{children || result}</span>;
}
export function SystemBadge({ kind = 'kill-safe', children, ...rest }) {
  const map = {
    'kill-armed': ['sn-sys-armed', 'Power', 'KILL SWITCH ARMED'], 'kill-safe': ['sn-sys-safe', 'PowerOff', 'Kill switch safe'],
    'job-failed': ['sn-sys-alarm', 'TriangleAlert', 'JOB FAILED'], 'holiday': ['sn-sys-notice', 'CalendarOff', 'MARKET HOLIDAY'],
    'early-close': ['sn-sys-notice', 'Clock', 'EARLY CLOSE'],
  };
  const [cls, icon, text] = map[kind];
  return <span {...rest} className={cx('sn-badge', cls)} role={kind === 'kill-armed' ? 'status' : undefined}><Icon name={icon} />{children || text}</span>;
}
export function RoleChip({ role = 'viewer' }) {
  return <span className={cx('sn-badge sn-role', role === 'owner' && 'sn-role-owner')}>{role[0].toUpperCase() + role.slice(1)}</span>;
}
/** Renders nothing without a `version`, so a build never shows a made-up number. */
export function VersionChip({ label = 'Sentinel', version, build, env }) {
  if (!version) return null;
  return <span className="sn-badge sn-version" title={build ? `build ${build}` : undefined}>{label} <b>v{version}</b>{env && <> · {env}</>}</span>;
}
export function Avatar({ name = '', initials, size = 'md' }) {
  const ini = (initials || String(name).split(/[\s@._-]+/).filter(Boolean).map(s => s[0]).slice(0, 2).join('')).toUpperCase();
  return <span className={cx('sn-avatar', size !== 'md' && 'sn-avatar-' + size)} aria-hidden="true">{ini}</span>;
}

/* ---------- Tabs ---------- */
export function Tabs({ tabs, value, onChange, label = 'Sections', ...rest }) {
  const [inner, setVal] = useState(value ?? tabs[0].value);
  const val = value ?? inner;
  const refs = useRef([]);
  const idx = tabs.findIndex(t => t.value === val);
  const go = n => { setVal(tabs[n].value); onChange && onChange(tabs[n].value); refs.current[n]?.focus(); };
  return (
    <div {...rest} className="sn-tabs" role="tablist" aria-label={label} onKeyDown={e => {
      if (e.key === 'ArrowRight') go((idx + 1) % tabs.length); if (e.key === 'ArrowLeft') go((idx - 1 + tabs.length) % tabs.length);
      if (e.key === 'Home') go(0); if (e.key === 'End') go(tabs.length - 1);
    }}>
      {tabs.map((t, i) => (
        <button key={t.value} ref={el => refs.current[i] = el} role="tab" type="button" className="sn-tab" data-testid={t.testId} aria-selected={t.value === val} tabIndex={t.value === val ? 0 : -1}
          onClick={() => go(i)}>{t.icon && <Icon name={t.icon} />}{t.label}{t.count != null && <span className="sn-tab-count">{t.count}</span>}</button>
      ))}
    </div>
  );
}

/* ---------- Overlays ---------- */
export function Tooltip({ content, children, open: forced }) {
  const [open, setOpen] = useState(false); const id = useId();
  const show = forced ?? open;
  return (
    <span className="sn-tip-anchor" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      onKeyDown={e => e.key === 'Escape' && setOpen(false)}>
      {React.isValidElement(children) ? React.cloneElement(children, { 'aria-describedby': show ? id : undefined }) : children}
      {show && <span className="sn-tip-float"><span className="sn-tooltip" role="tooltip" id={id}>{content}</span></span>}
    </span>
  );
}
export function Toast({ tone = 'neutral', title, children, time, onDismiss, icon }) {
  const ic = icon || { neutral: 'Info', alarm: 'TriangleAlert', provenance: 'FlaskRound', success: 'ClipboardCheck' }[tone];
  return (
    <div className="sn-toast" data-tone={tone} role={tone === 'alarm' ? 'alert' : 'status'} aria-live={tone === 'alarm' ? 'assertive' : 'polite'}>
      <Icon name={ic} size={18} />
      <div className="sn-toast-t">{title}</div>
      <IconButton icon="X" label="Dismiss" size="sm" onClick={onDismiss} />
      <div className="sn-toast-b">{children}{time && <span className="sn-num" style={{ display: 'block', marginTop: 4, color: 'var(--text-tertiary)', fontSize: 11 }}>{time}</span>}</div>
    </div>
  );
}
export function Dialog({ title, children, footer, icon, tone, onClose, labelledBy, ...rest }) {
  const ref = useRef(null); const id = useId();
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const f = el.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
    const onKey = e => {
      if (e.key === 'Escape') onClose && onClose();
      if (e.key === 'Tab' && f.length) { const a = f[0], z = f[f.length - 1]; if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); } else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); } }
    };
    el.addEventListener('keydown', onKey); return () => el.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div {...rest} className="sn-dialog" role={tone === 'alarm' ? 'alertdialog' : 'dialog'} aria-modal="true" aria-labelledby={id} ref={ref}>
      <div className="sn-dialog-h">
        {icon && <Icon name={icon} size={20} style={{ color: tone === 'alarm' ? 'var(--alarm-fg)' : 'var(--text-secondary)', marginTop: 2 }} />}
        <h2 id={id} style={{ flex: 1 }}>{title}</h2>
        <IconButton icon="X" label="Close dialog" size="sm" onClick={onClose} />
      </div>
      <div className="sn-dialog-b">{children}</div>
      {footer && <div className="sn-dialog-f">{footer}</div>}
    </div>
  );
}
/** A Dialog on a scrim, centred in the viewport. Clicking the scrim or pressing Esc calls onClose. */
export function Modal({ onClose, width = 560, ...props }) {
  useEffect(() => {
    const prev = document.activeElement;
    const first = document.querySelector('.sn-modal .sn-dialog-b input, .sn-modal .sn-dialog-b textarea, .sn-modal .sn-dialog-b button, .sn-modal .sn-dialog-f button');
    first && first.focus();
    return () => { prev && prev.focus && prev.focus(); };
  }, []);
  return (
    <div className="sn-modal" onMouseDown={e => { if (e.target === e.currentTarget && onClose) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-dialog)', background: 'var(--surface-scrim)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh var(--space-4) var(--space-4)', overflowY: 'auto' }}>
      <div style={{ width, maxWidth: '100%' }}><Dialog onClose={onClose} style={{ width: '100%', maxWidth: '100%' }} {...props} /></div>
    </div>
  );
}
/**
 * A confirmation step in a Modal — the replacement for window.confirm / window.prompt.
 * `field` asks for text first: { label, hint, type, required, minLength, match, initial, multiline, options }.
 * `match` makes it type-to-confirm (the value must equal it exactly). `options` renders a radio group instead of a text box.
 * onConfirm(value) may return a promise; the dialog stays open and busy until it settles, and closes on success.
 */
export function ConfirmModal({ title, children, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone, icon, field, onConfirm, onClose, width = 480 }) {
  const [val, setVal] = useState(field?.initial ?? (field?.options ? field.options[0]?.value : ''));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const fid = useId();
  const v = typeof val === 'string' ? val.trim() : val;
  const invalid = !!field && (
    (field.required !== false && !v) ||
    (field.minLength && String(val || '').length < field.minLength) ||
    (field.match != null && val !== field.match) ||
    (field.validate && field.validate(val)));
  const go = async () => {
    if (invalid || busy) return;
    setBusy(true); setErr(null);
    try { await (onConfirm && onConfirm(field ? (typeof val === 'string' && field.type !== 'password' ? val.trim() : val) : undefined)); onClose && onClose(); }
    catch (e) { setErr(e?.message || String(e)); setBusy(false); }
  };
  const help = field && (typeof field.validate === 'function' && val ? field.validate(val) : null);
  return (
    <Modal width={width} onClose={busy ? undefined : onClose} title={title} tone={tone} icon={icon || (tone === 'alarm' ? 'TriangleAlert' : undefined)}
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>{cancelLabel}</Button>
        <Button variant={tone === 'alarm' ? 'destructive' : 'primary'} onClick={go} disabled={invalid} loading={busy} data-testid="confirm-modal-ok">{confirmLabel}</Button>
      </>}>
      <form className="sn-stack" onSubmit={e => { e.preventDefault(); go(); }}>
        {children && <div className="sn-small sn-muted" style={{ margin: 0 }}>{children}</div>}
        {field && field.options && (
          <div className="sn-stack" role="radiogroup" aria-label={field.label} style={{ gap: 4 }}>
            <span className="sn-field-label">{field.label}</span>
            {field.options.map(o => (
              <button key={o.value} type="button" role="radio" aria-checked={val === o.value} className="sn-check-row" onClick={() => setVal(o.value)}>
                <span className="sn-checkbox" style={{ borderRadius: '50%' }}>{val === o.value && <Icon name="Check" size={12} strokeWidth={2.5} />}</span>
                <span style={{ display: 'flex', flexDirection: 'column' }}><span>{o.label}</span>{o.description && <span className="sn-help">{o.description}</span>}</span>
              </button>
            ))}
          </div>
        )}
        {field && !field.options && field.multiline && (
          <div className="sn-field">
            <label className="sn-field-label" htmlFor={fid}>{field.label}{field.required !== false && <span className="sn-req">Required</span>}</label>
            <div className="sn-input-wrap" style={{ height: 'auto', alignItems: 'stretch' }}>
              <textarea id={fid} className="sn-input" rows={3} value={val} onChange={e => setVal(e.target.value)} aria-describedby={field.hint ? fid + '-h' : undefined}
                style={{ height: 'auto', padding: 'var(--space-2)', resize: 'vertical' }} data-testid="confirm-modal-input" />
            </div>
            {field.hint && <span className="sn-help" id={fid + '-h'}>{field.hint}</span>}
          </div>
        )}
        {field && !field.options && !field.multiline && (
          <TextField id={fid} label={field.label} type={field.type || 'text'} value={val} onChange={e => setVal(e.target.value)} required={field.required !== false}
            autoComplete={field.autoComplete || 'off'} spellCheck={false} hint={field.hint} error={help || undefined} minLength={field.minLength}
            style={field.match != null ? { fontFamily: 'var(--font-mono)' } : undefined} data-testid="confirm-modal-input" />
        )}
        {err && <span className="sn-error" role="alert"><Icon name="TriangleAlert" size={14} />{err}</span>}
      </form>
    </Modal>
  );
}

export function Sheet({ side = 'right', title, children, footer, onClose }) {
  const id = useId();
  return (
    <div className={cx('sn-sheet', 'sn-sheet-' + side)} role="dialog" aria-modal="true" aria-labelledby={id}>
      {side === 'bottom' && <div className="sn-sheet-grip" aria-hidden="true" />}
      <div className="sn-dialog-h" style={{ paddingTop: side === 'bottom' ? 8 : undefined }}><h2 id={id} style={{ flex: 1 }}>{title}</h2><IconButton icon="X" label="Close" size="sm" onClick={onClose} /></div>
      <div className="sn-dialog-b">{children}</div>
      {footer && <div className="sn-dialog-f">{footer}</div>}
    </div>
  );
}
export function Skeleton({ width = '100%', height = 14, lines, radius }) {
  if (lines) return <div className="sn-stack" style={{ gap: 8 }} aria-busy="true" aria-label="Loading">{Array.from({ length: lines }, (_, i) => <span key={i} className="sn-skel" style={{ width: i === lines - 1 ? '60%' : width, height }} />)}</div>;
  return <span className="sn-skel" style={{ width, height, borderRadius: radius }} aria-hidden="true" />;
}
export function EmptyState({ icon = 'Layers', title, children, action, ...rest }) {
  return (
    <div {...rest} className="sn-empty">
      <span className="sn-empty-icon"><Icon name={icon} size={18} /></span>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}
export function ErrorState({ title = 'This panel failed to render', children, detail, onRetry, reportId }) {
  return (
    <div className="sn-errorstate" role="alert">
      <Icon name="TriangleAlert" />
      <div className="sn-stack" style={{ gap: 8 }}>
        <h3>{title}</h3>
        <p>{children}</p>
        {detail && <pre>{detail}</pre>}
        <div className="sn-row">{onRetry && <Button size="sm" icon="RefreshCw" onClick={onRetry}>Retry panel</Button>}{reportId && <Button size="sm" variant="ghost" icon="Copy" onClick={() => navigator.clipboard?.writeText(String(reportId))}>Copy report {reportId}</Button>}</div>
      </div>
    </div>
  );
}

/* ---------- Kill switch: hold-to-arm, type-to-confirm to disarm ---------- */
export function KillSwitch({ armed = false, armedBy, armedAt, canOperate = true, stage: initialStage, onArm, onDisarm, reason }) {
  const [holding, setHolding] = useState(false);
  const [stage, setStage] = useState(initialStage || 'idle');
  const [typed, setTyped] = useState('');
  const t = useRef(null); const kid = useId();
  const start = () => { if (!canOperate) return; setHolding(true); t.current = setTimeout(() => { setHolding(false); onArm && onArm(); }, 1500); };
  const stop = () => { setHolding(false); clearTimeout(t.current); };
  return (
    <section className="sn-kill" data-armed={armed} aria-labelledby={kid}>
      <div className="sn-kill-h">
        <Icon name={armed ? 'Power' : 'PowerOff'} size={20} style={{ color: armed ? 'var(--alarm-fg)' : 'var(--text-tertiary)' }} />
        <div style={{ flex: 1 }}>
          <div className="sn-kill-state" id={kid}>{armed ? 'KILL SWITCH ARMED' : 'Kill switch safe'}</div>
          <div className="sn-help">{armed ? `${armedBy || armedAt ? `Armed${armedBy ? ` by ${armedBy}` : ''}${armedAt ? ` at ${armedAt}` : ''}. ` : ''}${reason ? reason + '. ' : ''}Runs still execute and are recorded; every decision is forced to NO TRADE.` : 'When armed, every checkpoint is forced to NO TRADE until an owner disarms it.'}</div>
        </div>
        {armed ? <SystemBadge kind="kill-armed" /> : <SystemBadge kind="kill-safe" />}
      </div>
      {!armed && (
        <div className="sn-row">
          <button type="button" className="sn-btn sn-btn-destructive sn-hold" data-holding={holding} disabled={!canOperate}
            onPointerDown={start} onPointerUp={stop} onPointerLeave={stop}
            onKeyDown={e => { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) { e.preventDefault(); start(); } }} onKeyUp={e => { if (e.key === ' ' || e.key === 'Enter') stop(); }}
            aria-describedby={kid + '-help'}>
            <span className="sn-hold-fill" aria-hidden="true" /><Icon name="Power" />{holding ? 'Keep holding…' : 'Hold to arm kill switch'}
          </button>
          <span className="sn-help" id={kid + '-help'}>Press and hold 1.5 s (Space or Enter on keyboard). Owner role only.</span>
        </div>
      )}
      {armed && canOperate && stage === 'idle' && <div className="sn-row"><Button variant="secondary" icon="KeyRound" onClick={() => setStage('confirm')}>Disarm…</Button><span className="sn-help">Disarming requires typing DISARM.</span></div>}
      {armed && canOperate && stage === 'confirm' && (
        <div className="sn-row" style={{ alignItems: 'flex-end' }}>
          <TextField label="Type DISARM to confirm" value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off" spellCheck={false} style={{ fontFamily: 'var(--font-mono)' }} />
          <Button variant="primary" disabled={typed !== 'DISARM'} onClick={() => { setStage('idle'); setTyped(''); onDisarm && onDisarm(); }}>Disarm</Button>
          <Button variant="ghost" onClick={() => setStage('idle')}>Cancel</Button>
        </div>
      )}
    </section>
  );
}
